import React, { useState } from "react";
import Input from "@/client/components/frame-design-system/inputs/Input";
import TextArea from "@/client/components/frame-design-system/inputs/TextArea";
import CheckBox from "@/client/components/frame-design-system/inputs/Checkbox";
import Dropdown from "@/client/components/common/Dropdown";
import { SuccessCheckmark } from "@/client/components/icons/success-checkmark";
import Link from "next/link";
import { submitApplication } from "@/client/lib/api";

const ArtBlocksReleaseApplicationForm = () => {
  const [artistName, setArtistName] = useState("");
  const [email, setEmail] = useState("");
  const [discordHandle, setDiscordHandle] = useState("");
  const [twitterHandle, setTwitterHandle] = useState("");
  const [portfolio, setPortfolio] = useState("");
  const [creativeHistory, setCreativeHistory] = useState("");
  const [technicalProficiency, setTechnicalProficiency] = useState({
    name: "1 - Little Technical Experience",
    value: "1",
  });
  const [originalContent, setOriginalContent] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const proficiencyOptions = [
    { name: "1 - Little Technical Experience", value: "1" },
    { name: "2", value: "2" },
    { name: "3", value: "3" },
    { name: "4", value: "4" },
    { name: "5", value: "5" },
    { name: "6", value: "6" },
    { name: "7", value: "7" },
    { name: "8", value: "8" },
    { name: "9", value: "9" },
    { name: "10 - Extensive Technical Experience", value: "10" },
  ];

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!originalContent) {
      alert(
        "Please acknowledge the originality of your content before submitting."
      );
      return;
    }

    const formData = {
      artistName,
      email,
      discord: discordHandle,
      twitter: twitterHandle,
      portfolio,
      creativeHistory,
      technicalProficiency: technicalProficiency.value,
      originalityAck: originalContent,
    };

    try {
      setIsSubmitting(true);
      await submitApplication(formData);
      setIsSubmitted(true);
      setIsSubmitting(false);
    } catch (error) {
      console.error("Error submitting form:", error);
      setIsSubmitting(false);
    }
  };

  if (isSubmitted) {
    return (
      <div className="max-w-3xl mx-auto px-4 mt-8 sm:px-6 lg:px-8 text-center">
        <div className="mt-16">
          <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-green-100 mb-6">
            <SuccessCheckmark className="text-green-500" />
          </div>
          <h1 className="text-4xl dark:text-white font-bold mb-4">
            Congratulations!
          </h1>
          <p className="text-lg  text-neutral-600 dark:text-neutral-400  mb-8">
            You have successfully submitted your project application to Art
            Blocks! You should receive a confirmation and next steps in your
            email in about 10 minutes. If you do not receive this email in this
            timeframe please contact us.
          </p>
          <Link
            href="/"
            className="inline-block bg-black dark:bg-white text-white dark:text-black px-6 py-3 rounded-md font-medium"
          >
            Return to Homepage
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-8 md:py-16">
      <h1 className="text-4xl font-bold mb-4 dark:text-white">
        Apply to Release on Art Blocks
      </h1>
      <p className="text-base text-neutral-600 dark:text-neutral-400 mb-4">
        Complete the form below to start your application process
      </p>
      <div className="mt-8">
        <p className="text-p-s text-neutral-600 dark:text-neutral-400 mb-4">
          Once you&apos;ve submitted an application, you will receive an email
          invitation to create a project shell on our staging site. This is
          where you will upload your project to Art Blocks for review. The shell
          creation ensures that all projects are compatible with Art Blocks and
          that projects are reviewed using a standard format. Once you have
          completed your application, your submission will pass through an
          initial review to determine the right fit for your project.
        </p>
        <p className="text-p-s text-neutral-600 dark:text-neutral-400 mb-4">
          Art Blocks is dedicated to bringing the most compelling works of
          generative art to life. We expect that artists have a creative history
          and have the ability to provide an original generative script.
          Deploying an Art Blocks project is a fairly complex process, so please
          be upfront about your technical proficiency. You will need to either
          code the project yourself or have a partner who can assist. If you
          plan to work collaboratively, please provide complete information for
          all contributors. All submitted work must be original. We do not
          intend to release projects that use licensed, purchased, or recycled
          content.
        </p>
        <p className="text-p-s text-neutral-600 dark:text-neutral-400 mb-4">
          Once again, thank you for your interest in applying to release your
          art with Art Blocks!
        </p>
        <hr className="my-8 border-t border-gray-300" />
        <form onSubmit={handleSubmit} className="space-y-8">
          <div>
            <h2 className="text-2xl mb-2 dark:text-white">
              Artist Contact Information
            </h2>
            <p className="text-sm text-neutral-600 dark:text-neutral-400 mb-4">
              Please provide your contact details below. Initial contact from
              Art Blocks will be sent to your email address. Your contact
              information will be securely stored within Art Blocks&apos;
              internal systems and will not be shared publicly.
            </p>
            <div className="space-y-6 mt-6">
              <Input
                label="Artist Name(s)"
                placeholder="John Doe"
                value={artistName}
                onChange={(e) => setArtistName(e.target.value)}
                required
              />
              <Input
                label="Primary Email"
                placeholder="alex@artblocks.io"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                labelDescription="Initial communications will be sent to this email address."
              />
              <Input
                label="Discord Handle(s)"
                placeholder="Artist#9402"
                value={discordHandle}
                onChange={(e) => setDiscordHandle(e.target.value)}
              />
              <Input
                label="X Handle(s)"
                placeholder="@artist"
                value={twitterHandle}
                onChange={(e) => setTwitterHandle(e.target.value)}
              />
            </div>
          </div>
          <div>
            <h2 className="text-2xl mb-2 dark:text-white">Artist Background</h2>
            <p className="text-p-s text-neutral-600 dark:text-neutral-400 mb-4">
              Please share some additional information about yourself and your
              work so that we can better understand your creative background and
              your project.
            </p>
            <div className="space-y-6 mt-6">
              <TextArea
                label="Portfolio"
                placeholder="Type here..."
                value={portfolio}
                setValue={setPortfolio}
                labelDescription="Tell us about yourself. Please include a personal website and social media links or a short bio (about 250 words) that showcases your artistic background."
              />
              <TextArea
                label="Creative History"
                placeholder="Type here..."
                value={creativeHistory}
                setValue={setCreativeHistory}
                labelDescription="Please provide links that showcase your creative work. This could include links to previous projects, exhibition history, gallery affiliations, featured institutional collections, and press coverage."
              />
              <div>
                <label className="block text-sm font-medium text-neutral-700 dark:text-neutral-200 mb-2">
                  Technical Proficiency (1-10)
                </label>
                <p className="text-p-s text-neutral-600 dark:text-neutral-400 mb-2">
                  Please rate your technical/coding proficiency on a scale of 1
                  to 10. For collaborative projects, please complete for the
                  most technical member of the team.
                </p>
                <Dropdown
                  options={proficiencyOptions}
                  selectedOption={technicalProficiency}
                  setSelectedOption={setTechnicalProficiency}
                  width="full"
                />
              </div>
            </div>
          </div>
          <div className="flex items-start">
            <div className="flex items-center h-5">
              <CheckBox
                checked={originalContent}
                setChecked={setOriginalContent}
                onClick={(e) => e.preventDefault()}
              />
            </div>
            <div className="ml-3 text-sm">
              <label className="font-medium text-neutral-700 dark:text-neutral-200">
                I certify that my project will consist of 100% original content
                created by me or my collaborative team and will not contain
                content that is purchased or requires licensing from third
                parties.
              </label>
            </div>
          </div>
          <button
            type="submit"
            disabled={isSubmitting}
            className="w-full bg-gray-300 text-black py-3 px-4 rounded-md hover:bg-gray-400 transition duration-200 text-sm font-semibold focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-500"
          >
            Submit
          </button>
        </form>
      </div>
    </div>
  );
};

export default ArtBlocksReleaseApplicationForm;
